import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InternationalDataCard from "../../shared/component/InternationalDataCard";
import ViewToggle, { ViewMode } from "../../shared/component/ViewToggle";
import AlphabeticalCountryList from "../../shared/component/AlphabeticalCountryList";
import CountryDetailModal from "../../shared/component/CountryDetailModal";
import { DataItem } from "../../shared/definitions/internationalTypes";
import { groupByAlphabet } from "../../shared/utilities/alphabeticalGrouping";
import useQueryPoliceProfiles from "../hooks/useQueryPoliceProfiles";
import useQueryAllPoliceProfiles from "../hooks/useQueryAllPoliceProfiles";

interface PoliceProfilesListProps {
  className?: string;
}

const PoliceProfilesList: React.FC<PoliceProfilesListProps> = ({
  className = "",
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>("alphabetical");
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    items: DataItem[];
  } | null>(null);

  // Use different hooks based on view mode
  const gridQuery = useQueryPoliceProfiles();
  const allQuery = useQueryAllPoliceProfiles();

  // Switch between queries based on view mode
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = viewMode === "alphabetical" ? allQuery : gridQuery;

  // Flatten all pages data
  const allProfiles = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.kepolisian_internasional.data);
  }, [data]);

  // Group profiles by country/institution (lembaga) for grid view
  const groupedProfiles = useMemo(() => {
    const groups: Record<string, DataItem[]> = {};

    allProfiles.forEach((profile) => {
      const country = profile.lembaga;
      if (!groups[country]) {
        groups[country] = [];
      }
      groups[country].push(profile);
    });

    // Sort countries alphabetically
    const sortedCountries = Object.keys(groups).sort();
    const sortedGroups: Record<string, DataItem[]> = {};
    sortedCountries.forEach((country) => {
      sortedGroups[country] = groups[country];
    });

    return sortedGroups;
  }, [allProfiles]);

  // Group profiles alphabetically for alphabetical view
  const alphabeticalGroups = useMemo(() => {
    return groupByAlphabet(allProfiles);
  }, [allProfiles]);

  const handleViewProfile = (item: DataItem) => {
    navigate(`/tentang/profil-kepolisian-internasional/view/${item.id}`);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleCountryClick = (country: string, items: DataItem[]) => {
    setSelectedCountry({ name: country, items });
  };

  const handleCloseModal = () => {
    setSelectedCountry(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex justify-center items-center py-12 ${className}`}>
        <div className="loading loading-spinner loading-lg text-primary"></div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="alert alert-error max-w-md mx-auto">
          <svg
            className="w-12 h-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-lg font-semibold">Gagal memuat data</p>
            <p className="text-sm text-base-content/70 mt-2">
              Silakan coba lagi nanti
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (Object.keys(groupedProfiles).length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="alert alert-info max-w-md mx-auto">
          <svg
            className="w-12 h-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <div>
            <p className="text-lg font-semibold">Tidak ada data ditemukan</p>
            <p className="text-sm text-base-content/70 mt-2">
              Data profil kepolisian internasional belum tersedia
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* View Toggle */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-sm text-base-content/60">
            {viewMode === "grid"
              ? `${Object.keys(groupedProfiles).length} negara/institusi • ${allProfiles.length} dokumen`
              : `${Object.keys(alphabeticalGroups).length} huruf • ${Object.keys(groupedProfiles).length} negara/institusi • ${allProfiles.length} dokumen`}
          </p>
        </div>
        <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
      </div>

      {/* Content based on view mode */}
      {viewMode === "grid" ? (
        <>
          {/* Country Groups - Grid View */}
          {Object.entries(groupedProfiles).map(([country, profiles]) => (
            <div key={country} className="mb-8">
              {/* Country Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">{country}</h2>
                <div className="h-1 w-20 bg-primary rounded"></div>
                <p className="text-sm text-base-content/70 mt-2">
                  {profiles.length} dokumen tersedia
                </p>
              </div>

              {/* Profiles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                {profiles.map((profile) => (
                  <InternationalDataCard
                    key={profile.id}
                    item={profile}
                    onView={handleViewProfile}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasNextPage && (
            <div className="text-center py-8">
              <button
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                className="btn btn-primary"
              >
                {isFetchingNextPage ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Memuat...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Muat Lebih Banyak
                  </>
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        /* Alphabetical View */
        <AlphabeticalCountryList
          alphabeticalData={alphabeticalGroups}
          onCountryClick={handleCountryClick}
          listType="police"
        />
      )}

      {/* Country Detail Modal */}
      {selectedCountry && (
        <CountryDetailModal
          isOpen={!!selectedCountry}
          onClose={handleCloseModal}
          country={selectedCountry.name}
          items={selectedCountry.items}
          onViewDocument={handleViewProfile}
          listType="police"
        />
      )}
    </div>
  );
};

export default PoliceProfilesList;
